import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import ProductTemplate from '../../../components/feature/ProductTemplate';
import { useSEO } from '../../../utils/seo';
import { useState } from 'react';

const SteelBuildingPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    buildingSize: '',
    timeline: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    setErrorMessage('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6f4ftbv53dtlm7j9gtg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          buildingSize: '',
          timeline: '',
          message: '',
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to submit. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  useSEO({
    title: 'Steel Building Supplier | Wholesale Pre-Engineered Steel Buildings | Save 25-35% Factory Direct',
    description: 'Direct import steel building supplier for warehouses, manufacturing facilities, and commercial buildings. Wholesale pre-engineered steel structures with 25-35% cost savings. Bulk construction materials supplier serving custom builders, developers, and contractors across Canada. Factory-direct pricing on complete steel building packages.',
    keywords: 'steel building supplier, wholesale steel buildings, pre-engineered structures, bulk construction materials supplier, direct import building materials, metal buildings wholesale, save on building materials',
    canonical: `${siteUrl}/products/steel-building`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Pre-Engineered Steel Building Packages',
      description: 'Wholesale steel building supplier offering complete pre-engineered steel structures for industrial, commercial, and agricultural applications. Save 25-35% with factory-direct pricing on bulk construction materials.',
      category: 'Steel Buildings',
      brand: {
        '@type': 'Brand',
        name: 'Go Global Inc.',
      },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'CAD',
        availability: 'https://schema.org/InStock',
        seller: {
          '@type': 'Organization',
          name: 'Go Global Inc.',
        },
      },
    },
  });

  const productData = {
    category: 'Steel Building Packages',
    title: 'Pre-Engineered Steel Building Packages',
    subtitle: 'Save 25-35% on Complete Steel Structures',
    description: 'Direct import pre-engineered steel building systems for warehouses, manufacturing facilities, and commercial buildings. Complete packages include structural steel, fasteners, panels, doors, windows, and engineering drawings.',
    heroImage: 'https://public.readdy.ai/ai/img_res/a5269c61f85ec3bc6b93952216e361e6.jpg',
    
    whatIncluded: [
      'Complete structural steel framework',
      'Roof and wall panel systems',
      'All fasteners and connection hardware',
      'Entry doors and window systems',
      'Certified engineering drawings',
      'Erection instructions and support',
    ],

    idealFor: [
      'Industrial warehouse developers',
      'Manufacturing facility builders',
      'Commercial property investors',
      'Agricultural building contractors',
      'Self-storage facility developers',
    ],

    benefits: [
      {
        icon: 'ri-price-tag-3-line',
        title: 'Save 25-35% vs. Local Suppliers',
        description: 'Direct factory pricing eliminates distributor markup on complete building packages',
      },
      {
        icon: 'ri-time-line',
        title: 'Faster Construction Timeline',
        description: 'Pre-engineered systems reduce on-site construction time by 40-50%',
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Certified Engineering',
        description: 'All designs meet Canadian building codes with stamped engineering drawings',
      },
      {
        icon: 'ri-tools-line',
        title: 'Complete Building System',
        description: 'Everything needed for structure delivery: steel, panels, doors, windows, fasteners',
      },
    ],

    products: [
      {
        name: 'Clear Span Buildings',
        description: 'Column-free interior spaces up to 300 feet wide, ideal for warehouses and manufacturing',
        specs: 'Spans: 40\'-300\' | Heights: 16\'-40\' | Custom lengths',
        image: 'https://readdy.ai/api/search-image?query=large%20clear%20span%20steel%20building%20interior%20showing%20wide%20open%20space%20without%20columns%20and%20modern%20industrial%20design%20with%20natural%20lighting&width=600&height=500&seq=steel-clearspan-001&orientation=portrait',
      },
      {
        name: 'Multi-Span Buildings',
        description: 'Interior columns for larger footprints and cost efficiency on mega-warehouses',
        specs: 'Unlimited width | Cost-effective for 50,000+ sq ft',
        image: 'https://readdy.ai/api/search-image?query=multi%20span%20steel%20building%20structure%20with%20interior%20columns%20showing%20efficient%20design%20for%20large%20warehouse%20or%20manufacturing%20facility&width=600&height=500&seq=steel-multispan-001&orientation=portrait',
      },
      {
        name: 'Modular Expandable Buildings',
        description: 'Designed for future expansion as your operation grows',
        specs: 'Expandable end walls | Modular bay design',
        image: 'https://readdy.ai/api/search-image?query=modular%20steel%20building%20system%20showing%20expandable%20design%20with%20clean%20modern%20exterior%20and%20professional%20construction%20in%20industrial%20setting&width=600&height=500&seq=steel-modular-001&orientation=portrait',
      },
      {
        name: 'Mezzanine Systems',
        description: 'Structural mezzanines for additional floor space and storage capacity',
        specs: 'Load ratings: 125-250 PSF | Custom sizing',
        image: 'https://readdy.ai/api/search-image?query=industrial%20mezzanine%20platform%20with%20steel%20structure%20and%20safety%20railings%20in%20warehouse%20showing%20additional%20storage%20space%20and%20professional%20installation&width=600&height=500&seq=steel-mezzanine-001&orientation=portrait',
      },
      {
        name: 'Canopy & Loading Dock Structures',
        description: 'Steel canopies for loading docks, covered storage, and weather protection',
        specs: 'Custom projections | Integrated with main building',
        image: 'https://readdy.ai/api/search-image?query=modern%20steel%20canopy%20structure%20at%20commercial%20building%20entrance%20showing%20clean%20architectural%20design%20and%20professional%20fabrication&width=600&height=500&seq=steel-canopy-001&orientation=portrait',
      },
      {
        name: 'Doors, Windows & Accessories',
        description: 'Commercial doors, windows, insulation, and all finishing components',
        specs: 'Overhead doors | Man doors | Windows | Insulation',
        image: 'https://readdy.ai/api/search-image?query=steel%20building%20accessories%20including%20industrial%20doors%20insulation%20panels%20and%20trim%20components%20displayed%20showing%20complete%20building%20system&width=600&height=500&seq=steel-accessories-001&orientation=portrait',
      },
    ],

    specifications: [
      'Custom engineering for specific loads and spans',
      'Wind and snow load calculations included',
      'Seismic design available for high-risk zones',
      'Insulated panel options for climate control',
      'Crane runway systems available',
      'Complete erection drawings and instructions',
      'Fire-rated assemblies available',
      'Energy-efficient insulation packages',
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <ProductTemplate data={productData} />
      
      {/* Quote Request Form Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-gray-900 mb-4">
              Get Your Steel Building Quote
            </h2>
            <p className="text-lg text-gray-600">
              Tell us about your project and we'll provide a detailed cost comparison showing your savings
            </p>
          </div>

          <form 
            id="steel-building-quote"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-gray-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-gray-900 mb-2">
                  Building Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select building type</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="manufacturing">Manufacturing Facility</option>
                  <option value="commercial">Commercial Building</option>
                  <option value="agricultural">Agricultural Building</option>
                  <option value="self-storage">Self-Storage Facility</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="buildingSize" className="block text-sm font-semibold text-gray-900 mb-2">
                  Building Size *
                </label>
                <input
                  type="text"
                  id="buildingSize"
                  name="buildingSize"
                  value={formData.buildingSize}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="e.g., 100' x 200' x 24' high"
                />
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Timeline *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select timeline</option>
                  <option value="immediate">Immediate (0-3 months)</option>
                  <option value="short">Short-term (3-6 months)</option>
                  <option value="medium">Medium-term (6-12 months)</option>
                  <option value="long">Long-term (12+ months)</option>
                  <option value="planning">Planning phase</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                Project Details
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                placeholder="Tell us about your project requirements, special features needed, or any questions you have..."
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium">
                  ✓ Thank you! We'll send you a detailed quote within 24 hours.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">
                  {errorMessage}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitStatus === 'submitting'}
              className="w-full px-8 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-base font-semibold rounded-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {submitStatus === 'submitting' ? 'Submitting...' : 'Get My Steel Building Quote'}
              {submitStatus !== 'submitting' && <i className="ri-arrow-right-line ml-2"></i>}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              We'll respond within 24 hours with a detailed cost comparison
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SteelBuildingPage;